import React from 'react';
import { DatePicker, Input, InputNumber, Select, Switch } from 'antd';
import moment from 'moment';
import { components as Core, DATE_FORMATS, valueBuilder } from '../../../../core';
import { css, colors, fonts, spacing, StyleSheet } from '../../../../../styles';
import { FIELD_TYPES } from '../utils/constants';

/**
 * Renders a titled settings section.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string|null} props.title
 */
export const DetailsSection = ({ children, title = null }) => (
    <section className={css(styles.section)}>
        {title ? <h3 className={css(styles.sectionTitle)}>{title}</h3> : null}
        {children}
    </section>
);

/**
 * Renders a responsive grid of company-detail fields.
 *
 * @param {object} props
 * @param {Array<object>} props.fields
 * @param {Function} props.getFieldValue
 */
export const CompanyFieldGrid = ({ fields = [], getFieldValue = null }) => (
    <div className={css(styles.fieldGrid)}>
        {fields.map(field => (
            <CompanyField field={field} getFieldValue={getFieldValue} key={`${field.sourceField || 'company'}-${field.fieldName}`} />
        ))}
    </div>
);

/**
 * Renders one labeled company-detail field.
 *
 * @param {object} props
 * @param {object} props.field
 * @param {Function} props.getFieldValue
 */
export const CompanyField = ({ field, getFieldValue }) => {
    const inputId = `company-details-${field.sourceField || 'company'}-${field.fieldName}`;

    return (
        <div className={css(styles.formField)}>
            <span id={`${inputId}-label`} className={css(styles.fieldLabel)}>
                {field.label}
            </span>
            <CompanyFieldControl field={field} getFieldValue={getFieldValue} inputId={inputId} />
        </div>
    );
};

/**
 * Renders the Ant Design control that matches a field configuration.
 *
 * @param {object} props
 * @param {object} props.field
 * @param {Function} props.getFieldValue
 * @param {string} props.inputId
 */
export const CompanyFieldControl = ({ field, getFieldValue, inputId }) => {
    const value = getFieldValue ? getFieldValue(field) : undefined;
    const isDisabled = field.isReadOnly || !field.onChange;
    const ariaLabelledBy = `${inputId}-label`;

    const onChangeDate = date => {
        if (!field.onChange) return;
        field.onChange(date ? moment(date).format(field.showTime ? DATE_FORMATS.DATE_TIME : DATE_FORMATS.DATE_ANT) : null);
    };

    switch (field.type) {
        case FIELD_TYPES.date:
            return (
                <DatePicker
                    id={inputId}
                    aria-labelledby={ariaLabelledBy}
                    value={value ? moment(valueBuilder.getLocalFullTime(value)) : null}
                    showTime={field.showTime}
                    disabled={isDisabled}
                    onChange={onChangeDate}
                    showToday={false}
                    format={field.showTime ? DATE_FORMATS.DATE_TIME : DATE_FORMATS.DATE_ANT}
                    className={css(styles.formControl)}
                />
            );
        case FIELD_TYPES.number:
            return (
                <InputNumber
                    id={inputId}
                    aria-labelledby={ariaLabelledBy}
                    value={value}
                    min={field.min}
                    disabled={isDisabled}
                    onChange={field.onChange}
                    className={css(styles.formControl)}
                />
            );
        case FIELD_TYPES.select: {
            const options = field.options || [];
            const hasSelectedValue = value !== null && value !== undefined &&
                options.some(option => option.value === value);

            return (
                <Select
                    id={inputId}
                    aria-labelledby={ariaLabelledBy}
                    value={hasSelectedValue ? value : undefined}
                    disabled={isDisabled}
                    onChange={field.onChange}
                    placeholder={field.placeholder}
                    className={css(styles.formControl)}>
                    {options.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                            {option.text}
                        </Select.Option>
                    ))}
                </Select>
            );
        }
        case FIELD_TYPES.switch:
            return (
                <Switch
                    id={inputId}
                    aria-labelledby={ariaLabelledBy}
                    checked={Boolean(value)}
                    disabled={isDisabled}
                    onChange={field.onChange}
                />
            );
        default:
            return (
                <Input
                    id={inputId}
                    aria-labelledby={ariaLabelledBy}
                    value={value || ''}
                    disabled={isDisabled}
                    onChange={event => field.onChange && field.onChange(event.target.value)}
                    className={css(styles.formControl)}
                />
            );
    }
};

/**
 * Renders a field grid inside the shared collapse component.
 *
 * @param {object} props
 * @param {Array<object>} props.fields
 * @param {Function} props.getFieldValue
 * @param {string} props.itemKey
 * @param {string} props.title
 */
export const CollapsedFieldSection = ({ fields = [], getFieldValue = null, itemKey, title }) => (
    <Core.Collapse
        data={[{
            title,
            content: <CompanyFieldGrid fields={fields} getFieldValue={getFieldValue} />,
            key: itemKey
        }]}
    />
);

const styles = StyleSheet.create({
    section: {
        paddingTop: spacing.s4,
        marginTop: spacing.s4,
        borderTop: `1px solid ${colors.greyLight}`,
        ':first-child': {
            marginTop: 0,
            paddingTop: 0,
            borderTop: 'none'
        }
    },
    sectionTitle: {
        ...fonts.mdBold,
        color: colors.black,
        marginBottom: spacing.s3
    },
    fieldGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: spacing.s3
    },
    formField: {
        minWidth: 0,
        flex: `0 0 calc((100% - ${spacing.s3}px) / 2)`,
        '@media (max-width: 640px)': {
            flexBasis: '100%'
        }
    },
    fieldLabel: {
        display: 'block',
        ...fonts.smNormal,
        color: colors.blackOpacity,
        marginBottom: spacing.s1
    },
    formControl: {
        width: '100%',
        '&.ant-input, &.ant-input-number, &.ant-calendar-picker': {
            width: '100%',
            borderColor: colors.border,
            borderRadius: spacing.s0
        },
        '& .ant-select-selection': {
            borderColor: colors.border,
            borderRadius: spacing.s0
        }
    }
});
